import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  ChevronRight, Clock, FileArchive, FileText, FolderClosed, FolderOpen, FolderPlus,
  Image as ImageIcon, LayoutGrid, LogOut, Moon, Music, MoreHorizontal, Search, Settings,
  Sparkles, Star, Sun, Tag, Trash2, Upload, Users, Video,
} from 'lucide-react';
import type { Folder } from '@shared/types';
import { cn } from '@/lib/format';
import {
  useCreateFolderAt, useDeleteFolder, useFacets, useFolders, useLibraryStats,
  useLogout, useMe, useRenameFolder, useUpload,
} from '@/lib/queries';
import { useTheme } from '@/lib/theme';
import { Spinner } from './ui';

/**
 * Left rail — the DAM's spine. Four sections: Library (smart views over the whole
 * store), Folders (the editable asset tree), Browse (AI brand/type facets), and
 * Manage (admin tools). Counts come from /library/stats.
 */
export function Sidebar() {
  const { data: folderData, isLoading } = useFolders();
  const { data: me } = useMe();
  const { data: stats } = useLibraryStats();
  const { theme, toggle } = useTheme();
  const logout = useLogout();

  return (
    <nav className="flex h-full w-72 shrink-0 flex-col bg-brand-900 text-brand-50/80" aria-label="Navigation">
      <div className="flex h-14 items-center border-b border-white/10 px-3">
        <div className="flex items-center rounded-lg bg-white px-2.5 py-1.5">
          <img src="/ecomstat-logo.png" alt="ecomstat.io" className="h-5 w-auto" />
        </div>
      </div>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto pb-3">
        {/* ---- Library: smart views ---- */}
        <Section title="Library" />
        <div className="space-y-0.5 px-2">
          <ViewItem to="/" search={{}} icon={<LayoutGrid />} label="All Assets" count={stats?.total} exact />
          <ViewItem to="/" search={{ sort: 'created', order: 'desc' }} icon={<Clock />} label="Recent" />
          <ViewItem to="/" search={{ kind: 'image' }} icon={<ImageIcon />} label="Images" count={stats?.byKind.image} />
          <ViewItem to="/" search={{ kind: 'document' }} icon={<FileText />} label="Documents" count={stats?.byKind.document} />
          <ViewItem to="/" search={{ kind: 'video' }} icon={<Video />} label="Videos" count={stats?.byKind.video} />
          <ViewItem to="/" search={{ kind: 'audio' }} icon={<Music />} label="Audio" count={stats?.byKind.audio} />
          <ViewItem to="/" search={{ kind: 'archive' }} icon={<FileArchive />} label="Archives" count={stats?.byKind.archive} />
          <ViewItem to="/" search={{ untagged: true }} icon={<Sparkles />} label="Untagged" count={stats?.untagged} />
          <ViewItem to="/" search={{ favourites: true }} icon={<Star />} label="Favourites" count={stats?.favourites} />
          <NavItem to="/search" icon={<Search />} label="AI Search" />
        </div>

        {/* ---- Folders ---- */}
        <FolderSection folders={folderData?.tree ?? []} loading={isLoading} />

        {/* ---- Browse: AI facets ---- */}
        <BrowseSection />

        {/* ---- Manage (admin) ---- */}
        {me?.isAdmin && (
          <>
            <Section title="Manage" />
            <div className="space-y-0.5 px-2">
              <NavItem to="/review" icon={<Sparkles />} label="Tag Review" />
              <NavItem to="/prompt" icon={<Tag />} label="Tagging Prompt" />
              <NavItem to="/users" icon={<Users />} label="Users" />
              <NavItem to="/settings" icon={<Settings />} label="Settings" />
            </div>
          </>
        )}
      </div>

      {/* footer */}
      <div className="flex items-center gap-2 border-t border-white/10 p-2">
        <Link
          to="/account"
          className="min-w-0 flex-1 truncate rounded px-2 py-1 text-xs text-white/80 hover:bg-white/10 hover:text-white [&.active]:text-white"
          title="Account"
        >
          {me?.name ?? '…'}
          {me?.isAdmin && <span className="ml-1.5 rounded bg-white/15 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/70">admin</span>}
        </Link>
        <button onClick={toggle} className="grid size-7 place-items-center rounded text-white/50 hover:bg-white/10 hover:text-white" aria-label="Toggle theme">
          {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
        <button
          onClick={() => logout.mutate(undefined, { onSettled: () => window.location.assign('/login') })}
          className="grid size-7 place-items-center rounded text-white/50 hover:bg-white/10 hover:text-white"
          aria-label="Sign out"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </nav>
  );
}

function Section({ title, right }: { title: string; right?: React.ReactNode }) {
  return (
    <div className="mt-4 flex items-center justify-between px-4 pb-1 first:mt-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">{title}</span>
      {right}
    </div>
  );
}

const navBase =
  'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors hover:bg-white/10 hover:text-white [&>svg]:size-4 [&>svg]:shrink-0';

function NavItem({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link to={to} className={cn(navBase, '[&.active]:bg-white/20 [&.active]:text-white')}>
      {icon}
      <span className="truncate">{label}</span>
    </Link>
  );
}

/**
 * A Library smart-view link. All point at "/" with different search params; the
 * BrowsePage reads them. Active state is computed from the live location so only
 * the matching view highlights.
 */
function ViewItem({
  to, search, icon, label, count, exact,
}: {
  to: string; search: Record<string, unknown>; icon: React.ReactNode; label: string; count?: number; exact?: boolean;
}) {
  const loc = useRouterState().location;
  const cur = new URLSearchParams(loc.searchStr);
  const active =
    loc.pathname === to &&
    (exact
      ? [...cur.keys()].length === 0
      : Object.entries(search).every(([k, v]) => cur.get(k) === String(v)) && [...cur.keys()].length === Object.keys(search).length);
  return (
    <Link
      to={to}
      search={search}
      className={cn(navBase, active && 'bg-white/20 text-white')}
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {count != null && count > 0 && <span className="text-[10px] text-white/40 tabular-nums">{count}</span>}
    </Link>
  );
}

/* ---------- Folders section (editable tree) ---------- */

function FolderSection({ folders, loading }: { folders: Folder[]; loading: boolean }) {
  const createFolder = useCreateFolderAt();
  return (
    <>
      <Section
        title="Folders"
        right={
          <button
            onClick={() => {
              const name = window.prompt('New top-level folder name:');
              if (name?.trim()) createFolder.mutate({ name: name.trim(), parentId: null });
            }}
            className="grid size-5 place-items-center rounded text-white/40 hover:bg-white/10 hover:text-white"
            title="New folder"
            aria-label="New top-level folder"
          >
            <FolderPlus className="size-3.5" />
          </button>
        }
      />
      <div className="px-2">
        {loading ? (
          <div className="flex justify-center py-4"><Spinner className="text-white/40" /></div>
        ) : folders.length === 0 ? (
          <p className="px-2 py-2 text-[11px] text-white/30">No folders yet.</p>
        ) : (
          folders.map((f) => <FolderNode key={f.id} folder={f} depth={0} />)
        )}
      </div>
    </>
  );
}

function FolderNode({ folder, depth }: { folder: Folder; depth: number }) {
  const children = folder.children ?? [];
  const hasChildren = children.length > 0;
  const [open, setOpen] = useState(depth === 0);
  const [menu, setMenu] = useState(false);
  const createFolder = useCreateFolderAt();
  const rename = useRenameFolder();
  const del = useDeleteFolder();
  const upload = useUpload(folder.id);
  const me = useMe().data;

  function onUpload() {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = () => {
      const files = input.files;
      if (files) for (const f of Array.from(files)) void upload.mutateAsync(f).catch(() => {});
    };
    input.click();
    setMenu(false);
  }

  return (
    <div>
      <div className="group relative flex items-center rounded-lg hover:bg-white/10" style={{ paddingLeft: `${Math.min(depth, 5) * 0.7}rem` }}>
        {hasChildren ? (
          <button onClick={() => setOpen((v) => !v)} className="grid size-6 shrink-0 place-items-center text-white/40 hover:text-white" aria-label={open ? 'Collapse' : 'Expand'}>
            <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
          </button>
        ) : (
          <span className="size-6 shrink-0" />
        )}
        <Link
          to="/folders/$folderId"
          params={{ folderId: folder.id }}
          className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-[13px] hover:text-white [&.active]:font-medium [&.active]:text-white"
          title={folder.path}
        >
          {open && hasChildren ? <FolderOpen className="size-3.5 shrink-0 text-white/50" /> : <FolderClosed className="size-3.5 shrink-0 text-white/50" />}
          <span className="truncate">{folder.name}</span>
          {folder.assetCount ? <span className="text-[10px] text-white/40 tabular-nums">{folder.assetCount}</span> : null}
        </Link>
        <button
          onClick={() => setMenu((v) => !v)}
          className="mr-1 hidden size-6 shrink-0 place-items-center rounded text-white/40 hover:bg-white/15 hover:text-white group-hover:grid data-[open=true]:grid"
          data-open={menu}
          aria-label="Folder actions"
        >
          <MoreHorizontal className="size-3.5" />
        </button>

        {menu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
            <div className="absolute right-1 top-7 z-20 w-44 overflow-hidden rounded-lg border border-white/10 bg-brand-800 py-1 text-[13px] text-white/90 shadow-xl">
              <MenuBtn icon={<Upload className="size-3.5" />} label="Upload here" onClick={onUpload} />
              <MenuBtn
                icon={<FolderPlus className="size-3.5" />}
                label="New subfolder"
                onClick={() => {
                  const name = window.prompt(`New folder inside “${folder.name}”:`);
                  if (name?.trim()) createFolder.mutate({ name: name.trim(), parentId: folder.id });
                  setMenu(false);
                }}
              />
              <MenuBtn
                icon={<Tag className="size-3.5" />}
                label="Rename"
                onClick={() => {
                  const name = window.prompt('Rename folder:', folder.name);
                  if (name?.trim() && name.trim() !== folder.name) rename.mutate({ id: folder.id, name: name.trim() });
                  setMenu(false);
                }}
              />
              {me?.isAdmin && (
                <MenuBtn
                  icon={<Trash2 className="size-3.5" />}
                  label="Delete"
                  danger
                  onClick={() => {
                    if (window.confirm(`Delete “${folder.name}” and everything inside it? This cannot be undone.`)) del.mutate(folder.id);
                    setMenu(false);
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
      {hasChildren && open && <div>{children.map((c) => <FolderNode key={c.id} folder={c} depth={depth + 1} />)}</div>}
    </div>
  );
}

function MenuBtn({ icon, label, onClick, danger }: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={cn('flex w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-white/10', danger && 'text-red-300 hover:bg-red-500/20')}
    >
      {icon}
      {label}
    </button>
  );
}

/* ---------- Browse section: AI brand / image-type facets ---------- */

function BrowseSection() {
  const { data: facets } = useFacets();
  const [openB, setOpenB] = useState(false);
  const [openT, setOpenT] = useState(false);
  const brands = facets?.brands ?? [];
  const types = facets?.imageTypes ?? [];
  if (brands.length === 0 && types.length === 0) return null;

  return (
    <>
      <Section title="Browse" />
      <div className="space-y-0.5 px-2">
        {brands.length > 0 && (
          <Collapsible label="Brands" open={openB} onToggle={() => setOpenB((v) => !v)}>
            {brands.map((b) => (
              <FacetLink key={b} label={b} search={{ brand: b }} />
            ))}
          </Collapsible>
        )}
        {types.length > 0 && (
          <Collapsible label="Image types" open={openT} onToggle={() => setOpenT((v) => !v)}>
            {types.map((tp) => (
              <FacetLink key={tp} label={tp} search={{ imageType: tp }} />
            ))}
          </Collapsible>
        )}
      </div>
    </>
  );
}

function Collapsible({ label, open, onToggle, children }: { label: string; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div>
      <button onClick={onToggle} className={cn(navBase, 'w-full text-white/70')}>
        <ChevronRight className={cn('transition-transform', open && 'rotate-90')} />
        <span className="flex-1 text-left">{label}</span>
      </button>
      {open && <div className="ml-3 space-y-0.5 border-l border-white/10 pl-2">{children}</div>}
    </div>
  );
}

function FacetLink({ label, search }: { label: string; search: Record<string, string> }) {
  return (
    <Link
      to="/search"
      search={search}
      className="block truncate rounded px-2.5 py-1 text-[12px] text-white/60 hover:bg-white/10 hover:text-white"
      title={label}
    >
      {label}
    </Link>
  );
}
